'use client';

import Link from 'next/link';
import { useState } from 'react';
import CodePane from './CodePane';
import TocRail from './TocRail';
import { docsPath } from './data/nav';
import { QS, type QsLangTab } from './data/quickstarts';

const TABS: { key: QsLangTab; label: string }[] = [
  { key: 'curl', label: 'cURL' },
  { key: 'node', label: 'Node.js' },
  { key: 'python', label: 'Python' },
];

/**
 * Step-layout page (quickstarts + the MCP install page): numbered steps
 * with path chips, dark code panes, expected-result lines, and the
 * "ON THIS PAGE" rail. qs-api adds the cURL / Node.js (fetch) / Python
 * (requests) tab row — plain HTTP, no SDK; steps without a language variant
 * (e.g. storing the key) keep their shell pane on every tab.
 */
export default function QsPage({ pageId }: { pageId: string }) {
  const qs = QS[pageId];
  const [tab, setTab] = useState<QsLangTab>('curl');

  return (
    <div className="mt-[26px] grid max-w-[1090px] grid-cols-[minmax(0,1fr)_190px] gap-14 max-[999px]:grid-cols-1">
      <article className="min-w-0 max-w-[760px]">
        <span className="rounded-full border border-line-3 px-[9px] py-[3px] font-jetbrains text-[9.5px] tracking-[0.12em] text-muted">
          {qs.meta}
        </span>
        <h1 className="mb-0 mt-3.5 font-onest text-[31px] font-extrabold leading-[1.12] tracking-[-0.02em]">
          {qs.title}
        </h1>
        <p className="mt-3.5 text-[15px] leading-[1.65] text-muted [text-wrap:pretty]">{qs.lead}</p>

        {qs.langTabs && (
          <div className="mt-[26px] flex items-end gap-0.5 border-b border-line-2">
            {TABS.map((t) => (
              <button
                key={t.key}
                type="button"
                onClick={() => setTab(t.key)}
                className={`border-b-2 px-3.5 py-2 text-[13px] transition-colors ${
                  tab === t.key
                    ? 'border-brand-600 font-[650] text-ink'
                    : 'border-transparent text-dim hover:text-ink'
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        )}

        {qs.steps.map((s, i) => {
          const snippet = (tab !== 'curl' && s.codeTabs?.[tab]) || s.code;
          return (
            <section key={s.id} data-toc-id={s.id} className="mt-[42px] flex gap-[18px]">
              <span className="inline-flex h-[27px] w-[27px] flex-none items-center justify-center rounded-full border border-[color-mix(in_srgb,var(--color-brand-600)_35%,transparent)] bg-[color-mix(in_srgb,var(--color-brand-600)_8%,white)] font-jetbrains text-xs text-brand-700">
                {i + 1}
              </span>
              <div className="flex min-w-0 flex-1 flex-col gap-2.5">
                <h2 className="mb-0 mt-0.5 font-onest text-[19px] font-bold tracking-[-0.01em]">
                  {s.t}
                </h2>
                <span className="self-start rounded-md border border-line-2 bg-surface-2 px-[9px] py-[3px] font-jetbrains text-[11px] text-muted">
                  {s.path}
                </span>
                <p className="m-0 text-[14.5px] leading-[1.65] text-muted [text-wrap:pretty]">
                  {s.body}
                </p>
                {snippet && (
                  <CodePane src={snippet.src} lang={snippet.lang} label={snippet.label} copy shadow />
                )}
                {s.resp && <CodePane src={s.resp.src} lang={s.resp.lang} label={s.resp.label} />}
                {s.see && (
                  <div className="flex items-baseline gap-2">
                    <span className="flex-none font-jetbrains text-[9.5px] tracking-[0.1em] text-dim">
                      YOU SHOULD SEE —
                    </span>
                    <span className="text-[12.5px] text-muted">{s.see}</span>
                  </div>
                )}
              </div>
            </section>
          );
        })}

        <div className="mt-12 border-t border-line-2 pt-[18px]">
          <span className="font-jetbrains text-[10px] tracking-[0.14em] text-dim">WHERE NEXT</span>
          <div className="mt-2.5 flex flex-wrap gap-2">
            {qs.next.map((nx) => {
              const cls =
                'rounded-full border border-line-3 bg-surface px-[13px] py-[7px] text-[13px] font-[550] text-ink transition-colors hover:border-brand-600 hover:text-brand-700';
              return nx.id ? (
                <Link key={nx.label} href={docsPath(nx.id)} className={cls}>
                  {nx.label} →
                </Link>
              ) : (
                <a key={nx.label} href={nx.href} className={cls}>
                  {nx.label} ↗
                </a>
              );
            })}
          </div>
        </div>
      </article>

      <TocRail sections={qs.steps.map((s, i) => ({ id: s.id, label: `${i + 1}. ${s.t}` }))} />
    </div>
  );
}
